import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/* ──────────────────────────────────────────────
   Helpers
─────────────────────────────────────────────── */
const fmt    = (n) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
const fmt2   = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const fmtNum = (n) => new Intl.NumberFormat('tr-TR').format(Math.round(n));

function useCounter(target, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf, start;
    const tick = (now) => {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

/* ──────────────────────────────────────────────
   Icons (inline SVG, lucide-style)
─────────────────────────────────────────────── */
const Ico = {
  home:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  invoice: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>,
  way:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h13l1 4h4v8h-3"/><path d="M3 18h11"/><circle cx="7" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></svg>,
  quote:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M8 12h8M8 16h6"/></svg>,
  stock:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 7l-8-4-8 4v10l8 4 8-4z"/><path d="M4 7l8 4 8-4M12 11v10"/></svg>,
  ware:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M8 14h8M8 18h8"/></svg>,
  user:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/></svg>,
  bank:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="21" x2="21" y2="21"/><path d="M3 10h18M5 6l7-4 7 4M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"/></svg>,
  check:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,
  people:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>,
  chart:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  settings:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
  plus:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  search:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  bell:    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
  sun:     <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
  eye:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  edit:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4z"/></svg>,
  dots:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>,
  close:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  cal:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  chev:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
};

/* ──────────────────────────────────────────────
   Logo + Top bar (shared in dashboard tab)
─────────────────────────────────────────────── */
function Banner({ navigate, tab, onTabChange }) {
  return (
    <>
      <div className="dp-banner">
        <span className="dp-banner-tag">Önizleme</span>
        <span><b>Glass Dashboard</b></span>
        <span className="dp-banner-sub">Bu tasarım henüz uygulamaya geçirilmedi</span>
        <span className="dp-banner-spacer" />
        <button onClick={() => navigate('/dashboard')}>← Uygulamaya dön</button>
      </div>
      <div className="dp-tabs">
        <button className={`dp-tab ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => onTabChange('dashboard')}>{Ico.chart} Dashboard</button>
        <button className={`dp-tab ${tab === 'menu' ? 'active' : ''}`}      onClick={() => onTabChange('menu')}>{Ico.home} Ana Menü</button>
        <button className={`dp-tab ${tab === 'invoices' ? 'active' : ''}`}  onClick={() => onTabChange('invoices')}>{Ico.invoice} Fatura Listesi</button>
        <button className={`dp-tab ${tab === 'form' ? 'active' : ''}`}      onClick={() => onTabChange('form')}>{Ico.plus} Yeni Fatura (Modal)</button>
      </div>
    </>
  );
}

function GlassTopbar() {
  return (
    <div className="dp-topbar">
      <div className="dp-tb-logo">
        <div className="dp-tb-logo-mark" />
        <div className="dp-tb-logo-text">hent</div>
      </div>
      <div className="dp-tb-search">
        <span className="ico">{Ico.search}</span>
        <input placeholder="Fatura, cari, stok ara... (Ctrl + K)" />
        <span className="kbd">⌘K</span>
      </div>
      <div className="dp-tb-actions">
        <button className="dp-tb-icon-btn" title="Bildirimler">{Ico.bell}<span className="badge" /></button>
        <button className="dp-tb-icon-btn" title="Tema">{Ico.sun}</button>
        <div className="dp-tb-avatar">GK</div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Sidebar — Ana Menü
─────────────────────────────────────────────── */
function Sidebar({ active = 'invoices', onSelect }) {
  const links = [
    { id: 'home', label: 'Anasayfa', icon: Ico.home },
    { section: 'Satış' },
    { id: 'invoices', label: 'Satış Faturaları', icon: Ico.invoice, badge: '127' },
    { id: 'returns',  label: 'İade Faturaları',  icon: Ico.invoice },
    { id: 'waybills', label: 'İrsaliyeler',      icon: Ico.way },
    { id: 'quotes',   label: 'Teklifler',        icon: Ico.quote },
    { section: 'Stok' },
    { id: 'stocks',   label: 'Stoklar',          icon: Ico.stock },
    { id: 'wares',    label: 'Depolar',          icon: Ico.ware },
    { section: 'Cari & Finans' },
    { id: 'accounts', label: 'Müşteri / Tedarikçi', icon: Ico.user },
    { id: 'banks',    label: 'Banka Hesapları',   icon: Ico.bank },
    { id: 'checks',   label: 'Çek & Senet',       icon: Ico.check, badge: '3' },
    { section: 'Diğer' },
    { id: 'personnel',label: 'Personel',          icon: Ico.people },
    { id: 'reports',  label: 'Raporlar',          icon: Ico.chart },
  ];

  return (
    <aside className="dp-sidebar">
      <div className="dp-side-logo">
        <div className="dp-tb-logo-mark" />
        <div className="dp-tb-logo-text">hent</div>
      </div>
      <div className="dp-side-user">
        <div className="dp-tb-avatar" style={{ width: 36, height: 36 }}>GK</div>
        <div className="dp-side-user-info">
          <div className="dp-side-user-name">Gazi Kendirli</div>
          <div className="dp-side-user-role">Asistan Yazılım · Yönetici</div>
        </div>
      </div>
      <nav>
        {links.map((l, i) => l.section ? (
          <div key={`s-${i}`} className="dp-side-section">{l.section}</div>
        ) : (
          <div key={l.id} className={`dp-side-link ${l.id === active ? 'active' : ''}`} onClick={() => onSelect && onSelect(l.id)}>
            <span className="ico">{l.icon}</span>
            <span>{l.label}</span>
            {l.badge && <span className="dp-side-badge">{l.badge}</span>}
          </div>
        ))}
      </nav>
      <div className="dp-side-foot">
        <div className="dp-side-link">
          <span className="ico">{Ico.settings}</span>
          <span>Ayarlar</span>
        </div>
      </div>
    </aside>
  );
}

/* ──────────────────────────────────────────────
   Sparkline
─────────────────────────────────────────────── */
function Sparkline({ data, color }) {
  const w = 96, h = 28;
  const max = Math.max(...data), min = Math.min(...data);
  const r = max - min || 1;
  const pts = data.map((v, i) => `${(i/(data.length-1))*w},${h - ((v - min) / r) * (h - 4) - 2}`).join(' ');
  const gid = `gs-${color.replace(/[^a-z0-9]/gi, '')}`;
  return (
    <svg className="dp-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.45" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#${gid})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ──────────────────────────────────────────────
   Dashboard view
─────────────────────────────────────────────── */
function StatCard({ tint, label, value, prefix, delta, deltaDir, deltaSub, icon, spark, sparkColor }) {
  const count = useCounter(value);
  return (
    <div className={`dp-stat tint-${tint}`}>
      <div className="dp-stat-head">
        <div className="dp-stat-icon">{icon}</div>
        {spark && <Sparkline data={spark} color={sparkColor} />}
      </div>
      <div className="dp-stat-label">{label}</div>
      <div className="dp-stat-value">{prefix || ''}{fmtNum(count)}</div>
      <div className="dp-stat-foot">
        <span className={`dp-stat-delta ${deltaDir}`}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
            {deltaDir === 'up'   && <polyline points="6 14 12 8 18 14" />}
            {deltaDir === 'down' && <polyline points="6 10 12 16 18 10" />}
            {deltaDir === 'flat' && <line x1="6" y1="12" x2="18" y2="12" />}
          </svg>
          {delta}
        </span>
        <span className="dp-stat-delta-sub">{deltaSub}</span>
      </div>
    </div>
  );
}

function AreaChart({ data, labels }) {
  const w = 720, h = 200, pad = 28;
  const max = Math.max(...data), min = Math.min(...data);
  const r = max - min || 1;
  const stepX = (w - pad*2) / (data.length - 1);
  const pts = data.map((v, i) => ({ x: pad + i * stepX, y: pad + (1 - (v - min) / r) * (h - pad*2), v }));
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${pts[pts.length-1].x} ${h - pad} L ${pts[0].x} ${h - pad} Z`;
  return (
    <div className="dp-chart-wrap">
      <svg className="dp-chart-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="cg-line" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#06b6d4"/><stop offset="60%" stopColor="#8b5cf6"/><stop offset="100%" stopColor="#ec4899"/></linearGradient>
          <linearGradient id="cg-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.35"/><stop offset="100%" stopColor="#06b6d4" stopOpacity="0"/></linearGradient>
        </defs>
        <g className="dp-chart-grid">
          {[0,0.25,0.5,0.75,1].map((p, i) => <line key={i} x1={pad} x2={w-pad} y1={pad + p*(h-pad*2)} y2={pad + p*(h-pad*2)} />)}
        </g>
        <path d={areaPath} fill="url(#cg-area)" className="dp-chart-area" />
        <path d={linePath} className="dp-chart-line" stroke="url(#cg-line)" />
        {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="4" className="dp-chart-dot" stroke="#8b5cf6" />)}
      </svg>
      <div className="dp-chart-labels">{labels.map((l, i) => <span key={i}>{l}</span>)}</div>
    </div>
  );
}

function DashboardView({ compact }) {
  const chartData = [82, 124, 96, 168, 142, 198, 215];
  const chartLabels = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
  const greeting = (() => { const h = new Date().getHours(); if (h<6) return 'İyi geceler'; if (h<12) return 'Günaydın'; if (h<18) return 'İyi günler'; return 'İyi akşamlar'; })();
  const today = new Intl.DateTimeFormat('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date());

  return (
    <div className={compact ? 'dp-content' : 'dp-container'}>
      <div className="dp-hero">
        <div className="dp-hero-eyebrow">{today}</div>
        <h1>Bu hafta <em>₺ 247.890</em> ciro, 127 fatura.</h1>
        <div className="dp-hero-insight">
          <span className="pos">+%12,4</span> önceki haftaya göre. Tahsilat oranı <b>%71</b>. 7 fatura vadesi geçmiş — bugün incelemen önerilir.
        </div>
      </div>

      <div className="dp-stats">
        <StatCard tint="cyan"    label="Bu ay ciro"     value={1247890} prefix="₺ " delta="+12,4%"  deltaDir="up"   deltaSub="önceki ay" sparkColor="#0891b2" spark={[40,62,50,78,84,92,110,124]} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>} />
        <StatCard tint="emerald" label="Fatura sayısı"  value={127}     delta="+18"     deltaDir="up"   deltaSub="önceki ay" sparkColor="#059669" spark={[12,14,10,18,22,19,24,25]}    icon={Ico.invoice} />
        <StatCard tint="violet"  label="Tahsil edilen"  value={892450}  prefix="₺ " delta="%71"     deltaDir="flat" deltaSub="oran" sparkColor="#7c3aed" spark={[30,45,38,55,62,68,72,80]}    icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>} />
        <StatCard tint="amber"   label="Vade aşımı"     value={156300}  prefix="₺ " delta="7 fatura" deltaDir="down" deltaSub="bekliyor" sparkColor="#d97706" spark={[20,24,22,28,32,30,28,26]} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} />
      </div>

      <div className="dp-grid">
        <div className="dp-col-left">
          <div className="dp-glass">
            <div className="dp-glass-head">
              <div>
                <h2>Son 7 gün satış</h2>
                <div className="sub">Günlük ciro · ₺ cinsinden</div>
              </div>
              <button className="more">Bu hafta ▾</button>
            </div>
            <div className="dp-glass-body">
              <AreaChart data={chartData} labels={chartLabels} />
            </div>
          </div>
        </div>
        <div className="dp-col-right">
          <div className="dp-glass">
            <div className="dp-glass-head"><div><h2>Hızlı erişim</h2><div className="sub">En çok kullandığın işlemler</div></div></div>
            <div className="dp-actions-grid">
              <button className="dp-action-btn cyan">
                <div className="ico">{Ico.plus}</div>
                <div><div className="label">Yeni fatura</div><div className="desc">Satış faturası kes</div></div>
              </button>
              <button className="dp-action-btn emerald">
                <div className="ico">{Ico.user}</div>
                <div><div className="label">Yeni cari</div><div className="desc">Müşteri ekle</div></div>
              </button>
              <button className="dp-action-btn violet">
                <div className="ico">{Ico.check}</div>
                <div><div className="label">Tahsilat</div><div className="desc">Ödeme al</div></div>
              </button>
              <button className="dp-action-btn amber">
                <div className="ico">{Ico.chart}</div>
                <div><div className="label">Rapor</div><div className="desc">Aylık özet</div></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Invoice list
─────────────────────────────────────────────── */
const INVOICES = [
  { no: 'INV-2026-0142', date: '17.05.2026', name: 'Akın İnşaat Ltd. Şti.',   sub: 'Vade: 27.05.2026', status: 'paid',    amount:  14250 },
  { no: 'INV-2026-0141', date: '17.05.2026', name: 'Demir Otomotiv A.Ş.',     sub: 'Vade: 01.06.2026', status: 'pending', amount:   8420 },
  { no: 'INV-2026-0140', date: '16.05.2026', name: 'Yıldız Tekstil San.',     sub: 'Vade: 10.05.2026 (Geçti)', status: 'overdue', amount: 22890 },
  { no: 'INV-2026-0139', date: '15.05.2026', name: 'Berk Mühendislik',         sub: 'Vade: 25.05.2026', status: 'paid',    amount:   4560 },
  { no: 'INV-2026-0138', date: '15.05.2026', name: 'Aydın Gıda Ltd. Şti.',     sub: 'Vade: 24.05.2026', status: 'paid',    amount:   1890 },
  { no: 'INV-2026-0137', date: '14.05.2026', name: 'Çelik Metal İmalat',       sub: 'Taslak',           status: 'draft',   amount:  17600 },
  { no: 'INV-2026-0136', date: '13.05.2026', name: 'Polat Mobilya',            sub: 'Vade: 28.05.2026', status: 'pending', amount:   6730 },
  { no: 'INV-2026-0135', date: '13.05.2026', name: 'Karaca Elektrik',          sub: 'Vade: 08.05.2026 (Geçti)', status: 'overdue', amount: 31250 },
];

const AVATAR_PAIRS = [['neutral', 'neutral']]; // Mercury: tüm avatarlar nötr
const initials = (name) => name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

function InvoiceList({ onNewInvoice }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? INVOICES : INVOICES.filter(i => i.status === filter);

  return (
    <div className="dp-content">
      <div className="dp-inv-head">
        <div className="dp-inv-title">
          <h1>Satış Faturaları</h1>
          <p>Tüm satış faturalarını tek yerden yönet</p>
        </div>
        <div className="dp-inv-head-actions">
          <button className="dp-cta ghost">Dışa aktar</button>
          <button className="dp-cta" onClick={onNewInvoice}>{Ico.plus} Yeni Fatura</button>
        </div>
      </div>

      <div className="dp-inv-mini-stats">
        <div className="dp-mini-stat"><div className="dp-mini-stat-label">Toplam</div><div className="dp-mini-stat-value">{fmt(1247890)}</div></div>
        <div className="dp-mini-stat"><div className="dp-mini-stat-label">Tahsil</div><div className="dp-mini-stat-value" style={{ color: '#059669' }}>{fmt(892450)}</div></div>
        <div className="dp-mini-stat"><div className="dp-mini-stat-label">Bekleyen</div><div className="dp-mini-stat-value" style={{ color: '#d97706' }}>{fmt(199140)}</div></div>
        <div className="dp-mini-stat"><div className="dp-mini-stat-label">Vade Aşımı</div><div className="dp-mini-stat-value" style={{ color: '#dc2626' }}>{fmt(156300)}</div></div>
      </div>

      <div className="dp-filter-bar">
        <div className="dp-filter-chips">
          <button className={`dp-chip ${filter === 'all'     ? 'active' : ''}`} onClick={() => setFilter('all')}>Tümü <span className="dp-chip-count">{INVOICES.length}</span></button>
          <button className={`dp-chip ${filter === 'paid'    ? 'active' : ''}`} onClick={() => setFilter('paid')}>Ödendi <span className="dp-chip-count">{INVOICES.filter(i=>i.status==='paid').length}</span></button>
          <button className={`dp-chip ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>Bekliyor <span className="dp-chip-count">{INVOICES.filter(i=>i.status==='pending').length}</span></button>
          <button className={`dp-chip ${filter === 'overdue' ? 'active' : ''}`} onClick={() => setFilter('overdue')}>Gecikti <span className="dp-chip-count">{INVOICES.filter(i=>i.status==='overdue').length}</span></button>
          <button className={`dp-chip ${filter === 'draft'   ? 'active' : ''}`} onClick={() => setFilter('draft')}>Taslak <span className="dp-chip-count">{INVOICES.filter(i=>i.status==='draft').length}</span></button>
        </div>
        <div className="dp-search-glass">
          <span className="ico">{Ico.search}</span>
          <input placeholder="Fatura no veya cari ara..." />
        </div>
      </div>

      <div className="dp-inv-table">
        <table>
          <thead>
            <tr>
              <th>Fatura No</th>
              <th>Tarih</th>
              <th>Cari</th>
              <th>Durum</th>
              <th className="right">Tutar</th>
              <th style={{ width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              return (
                <tr key={r.no}>
                  <td style={{ fontWeight: 600, color: 'var(--m-strong)', fontVariantNumeric: 'tabular-nums' }}>{r.no}</td>
                  <td><span style={{ color: 'var(--m-muted)' }}>{r.date}</span></td>
                  <td>
                    <div className="dp-cust">
                      <div className="dp-cust-av">{initials(r.name)}</div>
                      <div>
                        <div className="dp-cust-name">{r.name}</div>
                        <div className="dp-cust-sub">{r.sub}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`dp-inv-pill ${r.status}`}>
                      {r.status === 'paid'    && 'Ödendi'}
                      {r.status === 'pending' && 'Bekliyor'}
                      {r.status === 'overdue' && 'Gecikti'}
                      {r.status === 'draft'   && 'Taslak'}
                    </span>
                  </td>
                  <td className="dp-inv-amount">{fmt(r.amount)}</td>
                  <td>
                    <div className="dp-row-actions">
                      <button className="dp-row-act-btn" title="Görüntüle">{Ico.eye}</button>
                      <button className="dp-row-act-btn" title="Düzenle">{Ico.edit}</button>
                      <button className="dp-row-act-btn" title="Daha fazla">{Ico.dots}</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="dp-inv-foot">
          <span><b style={{ color: '#0f172a' }}>{filtered.length}</b> / {INVOICES.length} kayıt gösteriliyor</span>
          <div className="dp-inv-pager">
            <button disabled>‹</button>
            <button className="active">1</button>
            <button>2</button>
            <button>3</button>
            <button>›</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Invoice form modal — comprehensive
─────────────────────────────────────────────── */
const INITIAL_LINES = [
  { stok: 'Pergola Ares Profil 80x80', code: 'PRG-A8080', qty: 2,   unit: 'ad',     price: 5000.00, disc: 0,  vat: 20 },
  { stok: 'Bioclimatic Lamel',         code: 'PRG-BC-L',  qty: 4,   unit: 'm²',     price:  375.00, disc: 0,  vat: 20 },
  { stok: 'Montaj İşçilik',            code: 'SVC-MNT',   qty: 1,   unit: 'hizmet', price: 2500.00, disc: 0,  vat: 20 },
  { stok: 'Cam Yan Panel 200x100',     code: 'PRG-CMP',   qty: 2,   unit: 'ad',     price: 1850.00, disc: 5,  vat: 20 },
];

const MODAL_TABS = [
  { id: 0, label: 'Fatura Başlığı',     icon: 'general' },
  { id: 1, label: 'Kalemler',           icon: 'cart' },
  { id: 2, label: 'Fatura / Sevkiyat',  icon: 'package' },
  { id: 3, label: 'e-Belge',            icon: 'id' },
  { id: 4, label: 'Notlar',             icon: 'notes' },
];

function InvoiceModal({ onClose }) {
  const [activeTab, setActiveTab] = useState(0);
  const [docType, setDocType] = useState('e-fatura');
  const [payment, setPayment] = useState('vadeli');
  const [lines, setLines]     = useState(INITIAL_LINES);

  const calcLine = (l) => {
    const gross = l.qty * l.price;
    const afterDisc = gross * (1 - l.disc/100);
    const vatAmt = afterDisc * (l.vat/100);
    return { gross, afterDisc, vatAmt, total: afterDisc + vatAmt };
  };

  const subTotal  = lines.reduce((s, l) => s + calcLine(l).afterDisc, 0);
  const totalDisc = lines.reduce((s, l) => s + (l.qty * l.price * l.disc/100), 0);
  const totalVat  = lines.reduce((s, l) => s + calcLine(l).vatAmt, 0);
  const grand     = subTotal + totalVat;

  const updateLine = (i, key, val) => {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [key]: val } : l));
  };
  const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));
  const addLine    = () => setLines(prev => [...prev, { stok: '', code: '', qty: 1, unit: 'ad', price: 0, disc: 0, vat: 20 }]);

  const tabIcon = (k) => ({
    general: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    cart:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>,
    package: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
    id:      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="M15 8h2M15 12h2M7 16h10"/></svg>,
    notes:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>,
  }[k]);

  return (
    <div className="dp-modal-ov" onClick={onClose}>
      <div className="dp-modal dp-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="dp-modal-head">
          <h2>Yeni Satış Faturası</h2>
          <div className="sub">Müşterine yeni bir satış faturası kes · INV-2026-0143</div>
          <button className="dp-modal-close" onClick={onClose}>{Ico.close}</button>
        </div>

        {/* Sekme bar */}
        <div className="dp-modal-tabs">
          {MODAL_TABS.map(t => (
            <button key={t.id} className={`dp-modal-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              <span className="ico">{tabIcon(t.icon)}</span>
              {t.label}
            </button>
          ))}
        </div>

        <div className="dp-inv-modal-grid">
          {/* SOL — SEKMELİ FORM */}
          <div className="dp-inv-form">

            {/* TAB 0 — Fatura Başlığı (cari + belge tipi + tarihler) */}
            {activeTab === 0 && (
              <>
                <div className="dp-cust-card">
                  <div className="av">Aİ</div>
                  <div className="info">
                    <div className="name">Akın İnşaat Ltd. Şti.</div>
                    <div className="meta">VKN <b>1234567890</b> · Ümraniye V.D. · 0532 XXX XX XX</div>
                    <div className="addr">Atatürk Mah. Çankaya Cad. No:12 Ümraniye / İstanbul</div>
                  </div>
                  <button className="change">Değiştir</button>
                </div>

                <div className="dp-form-section-title">Belge Tipi</div>
                <div className="dp-doc-types" style={{ marginBottom: 4 }}>
                  <button className={`dp-doc-type ${docType === 'e-fatura' ? 'active' : ''}`} onClick={() => setDocType('e-fatura')}>e-Fatura</button>
                  <button className={`dp-doc-type ${docType === 'e-arsiv'  ? 'active' : ''}`} onClick={() => setDocType('e-arsiv')}>e-Arşiv</button>
                  <button className={`dp-doc-type ${docType === 'e-ihracat'? 'active' : ''}`} onClick={() => setDocType('e-ihracat')}>e-İhracat</button>
                  <button className={`dp-doc-type ${docType === 'kagit'    ? 'active' : ''}`} onClick={() => setDocType('kagit')}>Kağıt</button>
                </div>

                <div className="dp-form-section-title">Tarih & Belge</div>
                <div className="dp-form-grid cols-3">
                  <div><label className="dp-form-label">Belge No</label><input className="dp-form-input" defaultValue="INV-2026-0143" /></div>
                  <div><label className="dp-form-label">Fatura Tarihi</label><input className="dp-form-input" defaultValue="17.05.2026" /></div>
                  <div><label className="dp-form-label">Vade Tarihi</label><input className="dp-form-input" defaultValue="27.05.2026" /></div>
                  <div>
                    <label className="dp-form-label">Para Birimi</label>
                    <select className="dp-form-select" defaultValue="TRY">
                      <option value="TRY">₺ TL</option>
                      <option value="USD">$ USD</option>
                      <option value="EUR">€ EUR</option>
                    </select>
                  </div>
                  <div><label className="dp-form-label">Döviz Kuru</label><input className="dp-form-input" defaultValue="1,0000" /></div>
                  <div>
                    <label className="dp-form-label">Plasiyer</label>
                    <select className="dp-form-select" defaultValue="gk">
                      <option value="gk">Gazi Kendirli</option>
                      <option value="mb">Mehmet Bayrak</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* TAB 1 — Kalemler */}
            {activeTab === 1 && (
              <>
                <div className="dp-form-section-title with-action">
                  <span>Kalemler ({lines.length})</span>
                  <button className="inline-add" onClick={addLine}>{Ico.plus} Yeni kalem</button>
                </div>
                <div className="dp-line-rich">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ minWidth: 220 }}>Stok / Açıklama</th>
                        <th style={{ width: 70 }} className="right">Miktar</th>
                        <th style={{ width: 70 }}>Birim</th>
                        <th style={{ width: 110 }} className="right">Birim Fiyat</th>
                        <th style={{ width: 70 }} className="right">İnd %</th>
                        <th style={{ width: 60 }} className="right">KDV %</th>
                        <th style={{ width: 130 }} className="right">Tutar</th>
                        <th style={{ width: 36 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l, i) => {
                        const { total } = calcLine(l);
                        return (
                          <tr key={i}>
                            <td>
                              <input className="dp-line-input bold" value={l.stok} onChange={e => updateLine(i, 'stok', e.target.value)} placeholder="Stok seç..." />
                              <div className="dp-line-sub">{l.code || '—'}</div>
                            </td>
                            <td><input className="dp-line-input num" type="number" value={l.qty} onChange={e => updateLine(i, 'qty', parseFloat(e.target.value)||0)} /></td>
                            <td>
                              <select className="dp-line-input" value={l.unit} onChange={e => updateLine(i, 'unit', e.target.value)}>
                                <option value="ad">ad</option>
                                <option value="kg">kg</option>
                                <option value="m">m</option>
                                <option value="m²">m²</option>
                                <option value="m³">m³</option>
                                <option value="lt">lt</option>
                                <option value="hizmet">hiz</option>
                              </select>
                            </td>
                            <td><input className="dp-line-input num" type="number" value={l.price} onChange={e => updateLine(i, 'price', parseFloat(e.target.value)||0)} /></td>
                            <td><input className="dp-line-input num" type="number" value={l.disc} onChange={e => updateLine(i, 'disc', parseFloat(e.target.value)||0)} /></td>
                            <td>
                              <select className="dp-line-input" value={l.vat} onChange={e => updateLine(i, 'vat', parseFloat(e.target.value))}>
                                <option value={0}>0</option>
                                <option value={1}>1</option>
                                <option value={8}>8</option>
                                <option value={10}>10</option>
                                <option value={18}>18</option>
                                <option value={20}>20</option>
                              </select>
                            </td>
                            <td className="amt">{fmt2(total)} ₺</td>
                            <td>
                              <button className="dp-line-remove" onClick={() => removeLine(i)} title="Sil">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6"/></svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="dp-line-add-row">
                        <td colSpan={8}>
                          <button onClick={addLine}>{Ico.plus} Yeni kalem ekle</button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* TAB 2 — Fatura / Sevkiyat */}
            {activeTab === 2 && (
              <>
                <div className="dp-form-section-title">Sevkiyat</div>
                <div className="dp-form-grid">
                  <div>
                    <label className="dp-form-label">Çıkış Deposu</label>
                    <select className="dp-form-select" defaultValue="ana">
                      <option value="ana">Ana Depo</option>
                      <option value="sube">Şube Depo</option>
                      <option value="sevkiyat">Sevkiyat Bekleyen</option>
                    </select>
                  </div>
                  <div>
                    <label className="dp-form-label">Sevk Şekli</label>
                    <select className="dp-form-select" defaultValue="kargo">
                      <option value="elden">Elden Teslim</option>
                      <option value="kargo">Kargo</option>
                      <option value="kendi">Kendi Aracımız</option>
                      <option value="nakliyeci">Nakliyeci</option>
                    </select>
                  </div>
                  <div>
                    <label className="dp-form-label">Teslim Tarihi</label>
                    <input className="dp-form-input" defaultValue="20.05.2026" />
                  </div>
                  <div>
                    <label className="dp-form-label">Taşıyıcı / Plaka</label>
                    <input className="dp-form-input" placeholder="34 ABC 123" />
                  </div>
                  <div className="full">
                    <label className="dp-form-label">Teslim Adresi</label>
                    <textarea className="dp-form-textarea" defaultValue="Atatürk Mah. Çankaya Cad. No:12 Ümraniye / İstanbul" />
                  </div>
                </div>

                <div className="dp-form-section-title">Tevkifat & Stopaj</div>
                <div className="dp-form-grid cols-3">
                  <div>
                    <label className="dp-form-label">Tevkifat Kodu</label>
                    <select className="dp-form-select" defaultValue="">
                      <option value="">— Yok —</option>
                      <option value="601">601 (4/10)</option>
                      <option value="603">603 (5/10)</option>
                    </select>
                  </div>
                  <div>
                    <label className="dp-form-label">Stopaj Oranı</label>
                    <input className="dp-form-input" placeholder="%0" />
                  </div>
                  <div>
                    <label className="dp-form-label">KDV İade Türü</label>
                    <select className="dp-form-select" defaultValue=""><option value="">— Yok —</option></select>
                  </div>
                </div>
              </>
            )}

            {/* TAB 3 — e-Belge */}
            {activeTab === 3 && (
              <>
                <div className="dp-form-section-title">e-Fatura Bilgileri</div>
                <div className="dp-form-grid">
                  <div>
                    <label className="dp-form-label">Senaryo</label>
                    <select className="dp-form-select" defaultValue="ticari">
                      <option value="temel">Temel Fatura</option>
                      <option value="ticari">Ticari Fatura</option>
                      <option value="ihracat">İhracat Faturası</option>
                    </select>
                  </div>
                  <div>
                    <label className="dp-form-label">Fatura Tipi</label>
                    <select className="dp-form-select" defaultValue="satis">
                      <option value="satis">Satış</option>
                      <option value="iade">İade</option>
                      <option value="istisna">İstisna</option>
                    </select>
                  </div>
                  <div className="full">
                    <label className="dp-form-label">Alıcı Posta Kutusu (PK)</label>
                    <input className="dp-form-input" defaultValue="urn:mail:defaultpk@akin-insaat.com.tr" />
                  </div>
                  <div>
                    <label className="dp-form-label">GSM (e-Arşiv için)</label>
                    <input className="dp-form-input" defaultValue="0532 XXX XX XX" />
                  </div>
                  <div>
                    <label className="dp-form-label">E-posta</label>
                    <input className="dp-form-input" defaultValue="muhasebe@akin-insaat.com.tr" />
                  </div>
                </div>
                <div className="dp-form-section-title">Gönderim</div>
                <div className="dp-form-grid">
                  <div>
                    <label className="dp-form-label">Gönderim Modu</label>
                    <select className="dp-form-select" defaultValue="otomatik">
                      <option value="otomatik">Otomatik (kaydedince GİB'e gönder)</option>
                      <option value="manuel">Manuel (taslak kalsın)</option>
                    </select>
                  </div>
                  <div>
                    <label className="dp-form-label">Şablon</label>
                    <select className="dp-form-select" defaultValue="varsayilan">
                      <option value="varsayilan">Varsayılan Şablon</option>
                      <option value="renkli">Renkli Logolu Şablon</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* TAB 4 — Notlar */}
            {activeTab === 4 && (
              <>
                <div className="dp-form-section-title">Fatura Notu <span style={{ color: 'var(--m-subtle)', fontWeight: 400, letterSpacing: 0, textTransform: 'none' }}>· müşteri faturada görür</span></div>
                <textarea className="dp-form-textarea" defaultValue="Pergola sistem teslimi · 10 yıl çelik aksam garantisi kapsamındadır. Montaj 2 iş günü içinde tamamlanacaktır." style={{ minHeight: 100 }} />

                <div className="dp-form-section-title">Dahili Not <span style={{ color: 'var(--m-subtle)', fontWeight: 400, letterSpacing: 0, textTransform: 'none' }}>· sadece dahili görünür</span></div>
                <textarea className="dp-form-textarea" placeholder="Müşteri özel istekleri, hatırlatmalar..." defaultValue="Maliyet tablosu PRG-CALC-0017 · Karlılık %21" style={{ minHeight: 100 }} />

                <div className="dp-form-section-title">Etiketler</div>
                <div className="dp-doc-types">
                  <button className="dp-doc-type active">Pergola</button>
                  <button className="dp-doc-type">Bayi</button>
                  <button className="dp-doc-type">Garantili</button>
                  <button className="dp-doc-type">VIP</button>
                </div>
              </>
            )}
          </div>

          {/* SAĞ — ÖZET PANEL (sticky, sekme değişse de görünür) */}
          <aside className="dp-inv-summary">
            <div className="dp-side-note">
              <div className="ico">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 22 22 22 12 2"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
              <div className="body">
                <b>Stok uyarısı:</b> Bioclimatic Lamel için sadece <b>12 m²</b> stok kaldı, bu satış ile <b>8 m²</b>'ye düşecek.
              </div>
            </div>

            <div className="dp-form-section-title">Ödeme Şekli</div>
            <div className="dp-pay-options" style={{ marginBottom: 16 }}>
              <button className={`dp-pay-opt ${payment === 'pesin'  ? 'active' : ''}`} onClick={() => setPayment('pesin')}><span className="dot" /> Peşin</button>
              <button className={`dp-pay-opt ${payment === 'vadeli' ? 'active' : ''}`} onClick={() => setPayment('vadeli')}><span className="dot" /> Vadeli</button>
              <button className={`dp-pay-opt ${payment === 'kart'   ? 'active' : ''}`} onClick={() => setPayment('kart')}><span className="dot" /> Kart</button>
              <button className={`dp-pay-opt ${payment === 'havale' ? 'active' : ''}`} onClick={() => setPayment('havale')}><span className="dot" /> Havale / EFT</button>
            </div>

            <div className="dp-form-section-title">Özet</div>
            <div className="dp-summary-box">
              <div className="dp-sum-row"><span>{lines.length} kalem · ara toplam</span><span className="v">{fmt2(subTotal + totalDisc)} ₺</span></div>
              {totalDisc > 0 && (<div className="dp-sum-row discount"><span>Satır iskontoları</span><span className="v">- {fmt2(totalDisc)} ₺</span></div>)}
              <div className="dp-sum-row vat"><span>KDV (%20)</span><span className="v">+ {fmt2(totalVat)} ₺</span></div>
              <div className="dp-sum-row grand"><span className="label">Genel Toplam</span><span className="v">{fmt2(grand)} ₺</span></div>
            </div>

            <button className="dp-cta-big">{Ico.check} Faturayı Kes</button>
            <button className="dp-cta-ghost" style={{ marginBottom: 8 }}>Taslak Olarak Kaydet</button>
            <button className="dp-cta-ghost">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><path d="M6 14h12v8H6z"/></svg>
              Kaydet ve Yazdır
            </button>
          </aside>
        </div>

        <div className="dp-modal-foot">
          <div className="dp-modal-foot-info">
            {activeTab > 0 && <button className="dp-cta ghost" onClick={() => setActiveTab(t => t - 1)} style={{ marginRight: 8 }}>‹ Önceki sekme</button>}
            {activeTab < MODAL_TABS.length - 1 && <button className="dp-cta ghost" onClick={() => setActiveTab(t => t + 1)}>Sonraki sekme ›</button>}
            {activeTab === MODAL_TABS.length - 1 && <span>⌘ + Enter ile faturayı kes · Esc ile kapat</span>}
          </div>
          <div className="dp-modal-foot-btns">
            <button className="dp-cta ghost" onClick={onClose}>İptal</button>
            <button className="dp-cta">{Ico.check} Faturayı Kes — {fmt2(grand)} ₺</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   Main page
─────────────────────────────────────────────── */
export default function DesignPreviewPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [modalOpen, setModalOpen] = useState(false);

  // Form tab → otomatik modal aç
  useEffect(() => {
    if (tab === 'form') setModalOpen(true);
  }, [tab]);

  const closeModal = () => {
    setModalOpen(false);
    if (tab === 'form') setTab('invoices');
  };

  return (
    <div className="design-preview">
      <Banner navigate={navigate} tab={tab} onTabChange={setTab} />

      {/* Dashboard — hero-focused, full width */}
      {tab === 'dashboard' && (
        <>
          <GlassTopbar />
          <DashboardView />
        </>
      )}

      {/* Menu — sidebar visible + dashboard içeriği */}
      {tab === 'menu' && (
        <div className="dp-shell">
          <Sidebar active="home" />
          <main style={{ position: 'relative' }}>
            <DashboardView compact />
          </main>
        </div>
      )}

      {/* Invoice list — sidebar + table */}
      {(tab === 'invoices' || tab === 'form') && (
        <div className="dp-shell">
          <Sidebar active="invoices" />
          <main style={{ position: 'relative' }}>
            <InvoiceList onNewInvoice={() => setModalOpen(true)} />
            {modalOpen && <InvoiceModal onClose={closeModal} />}
          </main>
        </div>
      )}
    </div>
  );
}
